'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { CatButton } from 'components/blocks-components';
import { useFilterAndSortDrawer } from 'components/filter-and-sort/filter-and-sort-drawer-context/filter-and-sort-drawer-context';
import useFilter from 'hooks/use-filter';
import { SearchParams } from 'app/[lang]/[esite]/(core)/(search-display)/types';
import { isEqual } from 'lodash';
import { useLoaderOverlay } from 'app/[lang]/[esite]/contexts/loader-overlay-context';
import { SearchParams as CategorySearchParams } from 'app/[lang]/[esite]/(core)/category/[category]/types';

interface FilterAndSortControlsProps {
  searchParams: SearchParams | CategorySearchParams;
  isFederated?: boolean;
  isDiagram?: boolean;
}

export const FilterAndSortControls = ({
  searchParams,
  isFederated,
  isDiagram
}: FilterAndSortControlsProps) => {
  const initialParams = useRef(null);
  const t = useTranslations();
  const { closeDrawer, isDrawerOpen, setOnClose } = useFilterAndSortDrawer();
  const { handleResetFilters } = useFilter();
  const { setSelectedFacets, selectedFacets } = useLoaderOverlay();
  const federatedFacetParam = !isDiagram ? 'p_facet' : 'd_facet';
  const shouldResetFilters = !isEqual(initialParams.current, {
    ...searchParams,
    [isFederated ? federatedFacetParam : 'facet']: selectedFacets
  });

  const onClose = () => {
    if (shouldResetFilters && initialParams.current) {
      const { p_facet, d_facet, facet } = initialParams.current ?? {};
      const federatedSearchFacet = !isDiagram ? p_facet : d_facet;
      const facets = isFederated ? federatedSearchFacet : facet;
      setSelectedFacets(facets);
      handleResetFilters(initialParams.current);
    }
    initialParams.current = null;
  };

  useEffect(() => {
    if (isDrawerOpen) {
      initialParams.current = searchParams;
      setOnClose(() => onClose);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen]);

  const handleCancel = () => {
    if (shouldResetFilters) {
      const { p_facet, d_facet, facet } = initialParams.current ?? {};
      const federatedSearchFacet = !isDiagram ? p_facet : d_facet;
      const facets = isFederated ? federatedSearchFacet : facet;
      setSelectedFacets(facets);
      handleResetFilters(initialParams.current);
      initialParams.current = null;
    }
    closeDrawer();
  };

  const handleViewresults = () => {
    initialParams.current = null;
    closeDrawer();
  };

  return (
    <>
      <CatButton
        data-testid="view-results-button"
        onClick={handleViewresults}
        fullWidth
        variant="primary"
      >
        {t('VIEW_RESULTS')}
      </CatButton>
      <CatButton
        data-testid="cancel-button"
        variant="ghost"
        onClick={handleCancel}
      >
        {t('CANCEL')}
      </CatButton>
    </>
  );
};
